import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Bell, BellRing, CalendarClock, CheckCheck, FileCheck, FilePen, Megaphone, QrCode, UserCheck, X } from 'lucide-react';
import { OwnerButton, OwnerShell } from '../OwnerShell';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { toBn } from '../../../utils/bn';
import type { OwnerNav, OwnerNotification } from '../../../types/owner';
const catIcon: Record<string, typeof Bell> = {
  'আবেদন আপডেট': FileCheck,
  'তথ্য সংশোধনের অনুরোধ': FilePen,
  'গাড়ির অনুমোদন': FileCheck,
  'QR স্টিকার প্রস্তুত': QrCode,
  'স্টিকার সংগ্রহের নির্দেশনা': QrCode,
  'ফিটনেস মেয়াদ': CalendarClock,
  'ট্যাক্স টোকেন মেয়াদ': CalendarClock,
  'রুট পারমিট মেয়াদ': CalendarClock,
  'চালক অনুমোদন': UserCheck,
  'গুরুত্বপূর্ণ ডিএমপি বার্তা': Megaphone
};
const categories = Object.keys(catIcon);
type Filter = 'all' | 'unread' | 'important';
export function OwnerNotifications({
  nav
}: {
  nav: OwnerNav;
}) {
  const {
    notifications,
    markRead,
    markAllRead
  } = useOwnerService();
  const [filter, setFilter] = useState<Filter>('all');
  const [cat, setCat] = useState<string | null>(null);
  const [open, setOpen] = useState<OwnerNotification | null>(null);
  const unread = notifications.filter(n => !n.read).length;
  const rows = notifications.filter(n => (filter === 'unread' ? !n.read : filter === 'important' ? n.important : true) && (!cat || n.cat === cat));
  const openItem = (n: OwnerNotification) => {
    markRead(n.id);
    setOpen(n);
  };
  const act = (n: OwnerNotification) => {
    setOpen(null);
    if (!n.action) return;
    if (n.vehicleId) nav.setVehicleId(n.vehicleId);
    if (n.action === 'tracking') {
      const app = n.appId ?? (n.vehicleId === 'c15' ? 'DMP-OWN-2026-000117' : n.vehicleId === 'c1' ? 'DMP-OWN-2026-000098' : nav.focusAppId);
      nav.setFocusAppId(app);
    }
    if (n.action === 'vehicleDetail') nav.setDetailTab('info');
    nav.go(n.action);
  };
  return <OwnerShell nav={nav} title="নোটিফিকেশন" subtitle={`${toBn(unread)}টি অপঠিত`} back={false} tabs bodyClassName="p-0" right={<button onClick={markAllRead} className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-semibold hover:bg-white/10" aria-label="সব পঠিত হিসেবে চিহ্নিত করুন" data-id="element-1734" data-mp="11vj9u9lswq1s">
          <CheckCheck className="h-4 w-4" data-id="element-1735" data-mp="1bm4i6ora18xw" />সব পঠিত
        </button>} data-id="element-1733" data-mp="swo571yeno0l">
      <div className="sticky top-0 z-10 border-b border-border bg-white px-4 pb-2 pt-3" data-id="element-1736" data-mp="1bo75v24yipzg">
        <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1" role="tablist" data-id="element-1737" data-mp="1gn1yevsq7swr">
          {([['all', 'সব'], ['unread', 'অপঠিত'], ['important', 'গুরুত্বপূর্ণ']] as const).map(([k, l]) => <button key={k} role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} className={`rounded-md py-1.5 text-[12.5px] font-semibold ${filter === k ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500'}`} data-id="element-1738" data-mp="1pjj9y0505st1">{l}</button>)}
        </div>
        <div className="no-scrollbar -mx-4 mt-2 flex gap-1.5 overflow-x-auto px-4" data-id="element-1739" data-mp="3ju9c8tdj6dh7">
          <button onClick={() => setCat(null)} className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium ${!cat ? 'border-navy-800 bg-navy-800 text-white' : 'border-slate-200 text-slate-600'}`} data-id="element-1740" data-mp="3f1q0m1yelsf1">সকল বিভাগ</button>
          {categories.map(c => <button key={c} onClick={() => setCat(c === cat ? null : c)} className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium ${cat === c ? 'border-navy-800 bg-navy-800 text-white' : 'border-slate-200 text-slate-600'}`} data-id="element-1741" data-mp="6mphhxh55u3g">{c}</button>)}
        </div>
      </div>
      <ul className="divide-y divide-slate-100 bg-white" data-id="element-1742" data-mp="3oskofarat02l">
        {rows.map(n => {
        const Icon = catIcon[n.cat] ?? Bell;
        return <li key={n.id} data-id="element-1743" data-mp="2hgk88zuuqslz">
              <button onClick={() => openItem(n)} className={`flex w-full gap-3 px-4 py-3 text-left ${n.read ? '' : 'bg-blue-50/50'}`} data-id="element-1744" data-mp="2hcnzi25taosw">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${n.important ? 'bg-amber-100 text-amber-700' : 'bg-navy-50 text-navy-700'}`} data-id="element-1745" data-mp="547g6zjjdflg"><Icon className="h-5 w-5" data-id="element-1746" data-mp="9xijiqp53e8m" /></span>
                <span className="min-w-0 flex-1" data-id="element-1747" data-mp="3rcyut8hp2x9r">
                  <span className="flex items-center gap-1.5 text-[10.5px] font-medium text-slate-500" data-id="element-1748" data-mp="3ra2uxemadyio">
                    {n.cat}
                    {n.important && <AlertCircle className="h-3 w-3 text-amber-600" aria-label="গুরুত্বপূর্ণ" data-id="element-1749" data-mp="nw8jqf799u17" />}
                  </span>
                  <span className={`block text-[13.5px] leading-snug text-navy-900 ${n.read ? 'font-medium' : 'font-bold'}`} data-id="element-1750" data-mp="strydg7v88to">{n.title}</span>
                  <span className="block truncate text-[12px] text-slate-500" data-id="element-1751" data-mp="iw40iheyql7a">{n.body}</span>
                  <span className="block text-[10.5px] text-slate-400" data-id="element-1752" data-mp="2zkre6826vkvd">{n.time}</span>
                </span>
                {!n.read && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-600" aria-label="অপঠিত" data-id="element-1753" data-mp="33murz3cbz1jt" />}
              </button>
            </li>;
      })}
        {rows.length === 0 && <li className="flex flex-col items-center px-4 py-12 text-center text-sm text-slate-500" data-id="element-1754" data-mp="2r48xouwik5ut"><BellRing className="mb-2 h-8 w-8 text-slate-300" data-id="element-1755" data-mp="360q4r9xo6ybk" />কোনো নোটিফিকেশন নেই</li>}
      </ul>

      <AnimatePresence data-id="element-1756" data-mp="311dl0y23i7d7">
        {open && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-30 flex items-end bg-navy-900/50" onClick={() => setOpen(null)} data-id="element-1757" data-mp="312gdhaf2ghv8">
            <motion.div initial={{
          y: 60
        }} animate={{
          y: 0
        }} exit={{
          y: 60
        }} transition={{
          duration: 0.2
        }} onClick={e => e.stopPropagation()} className="w-full rounded-t-3xl bg-white p-5 pb-7" role="dialog" aria-label="নোটিফিকেশনের বিস্তারিত" data-id="element-1758" data-mp="28ekj4ccyz21m">
              <div className="flex items-start justify-between" data-id="element-1759" data-mp="1ull2g71gb1sh">
                <span className="rounded-full bg-navy-50 px-2.5 py-0.5 text-[11px] font-semibold text-navy-800" data-id="element-1760" data-mp="1uk0406c0mypc">{open.cat}</span>
                <button onClick={() => setOpen(null)} aria-label="বন্ধ করুন" className="rounded-full p-1 text-slate-400 hover:bg-slate-100" data-id="element-1761" data-mp="2favrrhjalrc4"><X className="h-5 w-5" data-id="element-1762" data-mp="2jbpdc499iu1u" /></button>
              </div>
              <h2 className="mt-3 font-heading text-lg font-bold text-navy-900" data-id="element-1763" data-mp="2o37d4ozjuol8">{open.title}</h2>
              <p className="mt-1.5 text-[14px] leading-relaxed text-slate-700" data-id="element-1764" data-mp="2t2akig3056wd">{open.body}</p>
              <p className="mt-2 text-[11px] text-slate-400" data-id="element-1765" data-mp="2t44tcw1018k3">{open.time} • ট্রাফিক বিভাগ, ডিএমপি</p>
              {open.action && <OwnerButton className="mt-5" onClick={() => act(open)} data-id="element-1766" data-mp="20dd1gaqyv62k">{open.action === 'qr' ? 'স্টিকারের বিস্তারিত দেখুন' : 'বিস্তারিত দেখুন'}</OwnerButton>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </OwnerShell>;
}